import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useScrollToTop } from '../hooks/useScrollToTop';
import { validateEmail, sanitizeEmail } from '../utils/emailValidation';

// API base URL - use relative URL for production to work with Vercel
const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:5001/api'
  : '/api';

const SubscribePage: React.FC = () => {
  // Scroll to top when page loads
  useScrollToTop();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage(null);

    // Email validation
    const validation = validateEmail(email);
    if (!validation.isValid) {
      setMessage({ type: 'error', text: validation.message });
      setIsSubmitting(false);
      return;
    }

    const sanitizedEmail = sanitizeEmail(email);

    try {
      console.log('Submitting to:', `${API_BASE_URL}/newsletter/subscribe`);
      
      const response = await fetch(`${API_BASE_URL}/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: sanitizedEmail, name: name.trim() }),
      });

      console.log('Response status:', response.status);
      
      const result = await response.json();
      console.log('Response data:', result);

      if (response.ok && result.success) {
        setMessage({ type: 'success', text: result.message });
        setEmail('');
        setName('');
      } else {
        setMessage({ type: 'error', text: result.message });
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setMessage({ type: 'error', text: 'Network error. Please check your connection and try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Subscribe to Destress Daily - Get Daily Destress Tips & Stress Relief</title>
        <meta name="description" content="Subscribe to Destress Daily and receive daily destress tips, stress relief strategies, and expert wellness advice delivered to your inbox." />
        <meta name="keywords" content="subscribe destress daily, destress tips newsletter, daily stress relief tips, subscribe, destress techniques, how to destress" />
        <meta property="og:title" content="Subscribe to Destress Daily - Get Daily Destress Tips & Stress Relief" />
        <meta property="og:description" content="Subscribe to Destress Daily and receive daily destress tips, stress relief strategies, and expert wellness advice." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://destress-daily.com/subscribe" />
        <link rel="canonical" href="https://destress-daily.com/subscribe" />
      </Helmet>

      <div className="pt-8">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-primary-50 via-white to-health-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-2xl mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Get Destress Daily
                <span className="block text-primary-600">Delivered to Your Inbox</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Join thousands of health-conscious individuals who receive our expert-curated daily health tips, 
                wellness advice, and actionable hacks to improve their well-being.
              </p>
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-600 rounded-lg mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Daily Delivery</h3>
              <p className="text-gray-600">Fresh health tips delivered every morning to start your day right</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-600 rounded-lg mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Expert Curated</h3>
              <p className="text-gray-600">Handpicked tips from health professionals and wellness experts</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-600 rounded-lg mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Actionable Tips</h3>
              <p className="text-gray-600">Simple, practical advice you can implement immediately</p>
            </div>
          </div>

          {/* Subscription Form */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 md:p-12">
            <div className="max-w-md mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                Subscribe Now
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Name (Optional)
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300"
                    placeholder="Your name"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300"
                    placeholder="your@email.com"
                  />
                </div>

                {message && (
                  <div className={`flex items-center p-4 rounded-xl ${
                    message.type === 'success' 
                      ? 'bg-green-50 border border-green-200' 
                      : 'bg-red-50 border border-red-200'
                  }`}>
                    {message.type === 'success' ? (
                      <CheckCircleIcon className="w-5 h-5 text-green-600 mr-3" />
                    ) : (
                      <ExclamationCircleIcon className="w-5 h-5 text-red-600 mr-3" />
                    )}
                    <span className={`text-sm font-medium ${
                      message.type === 'success' ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {message.text}
                    </span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary py-3 text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Subscribing...' : 'Subscribe to Newsletter'}
                </button>
              </form>

              <p className="text-xs text-gray-500 mt-4 text-center">
                By subscribing, you agree to receive our newsletter. You can unsubscribe at any time.
              </p>
            </div>
          </div>
        </div>

        {/* Testimonials Section */}
        <div className="bg-gray-50 py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
              What Our Subscribers Say
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold">S</span>
                  </div>
                  <div className="ml-3">
                    <p className="font-semibold text-gray-900">Sarah M.</p>
                    <p className="text-sm text-gray-500">Subscriber for 6 months</p>
                  </div>
                </div>
                <p className="text-gray-600">
                  "These daily stress relief tips have completely transformed my morning routine. 
                  I've learned so many simple tips that actually work!"
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold">M</span>
                  </div>
                  <div className="ml-3">
                    <p className="font-semibold text-gray-900">Mike R.</p>
                    <p className="text-sm text-gray-500">Subscriber for 1 year</p>
                  </div>
                </div>
                <p className="text-gray-600">
                  "I love how practical and actionable each tip is. No fluff, just real advice 
                  that I can use immediately to improve my health."
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SubscribePage; 